import { useEffect, useState } from 'react';
import { practiceService } from '@/core/services/practice';

/** The client types the API uses (ClientTypes): 0 Limited company, 1 Sole trader, 2 Partnership, 3 LLP. */
export const ClientKind = { LimitedCompany: 0, SoleTrader: 1, Partnership: 2, Llp: 3 } as const;

export interface ClientKindInfo {
  clientType: number;
  isCommunityInterest: boolean;
  companyType?: string | null;
  businessStartDate?: string | null;
  /** APT359: the registration number saved on the client (the Companies House link on the Companies House screens). */
  registrationNumber?: string | null;
}

const cache = new Map<string, ClientKindInfo>();
const loading = new Map<string, Promise<ClientKindInfo | null>>();

const listeners = new Set<() => void>();
/** BUG-027: a CIC flag saved on Company Info before the client's kind was read; applied when it is. */
const pendingCommunityInterest = new Map<string, boolean>();

const notify = () => listeners.forEach((listener) => listener());

export function resetClientKindCache(): void {
  cache.clear();
  loading.clear();
  pendingCommunityInterest.clear();
  notify();
}

/**
 * BUG-027: the CIC flag can be changed on Company Info, so the saved value is recorded here and every page reading the
 * client's kind (the Settings menu's CIC34 Report item among them) follows it at once, without asking the server again.
 */
export function setClientCommunityInterest(clientId: string, isCommunityInterest: boolean): void {
  const known = cache.get(clientId);
  if (known) {
    if (known.isCommunityInterest === isCommunityInterest) return;
    cache.set(clientId, { ...known, isCommunityInterest });
  } else {
    pendingCommunityInterest.set(clientId, isCommunityInterest);
  }
  notify();
}

/** A sole trader or a partnership: no Companies Act, directors, share capital or registration number. */
export const isUnincorporated = (clientType: number | null | undefined) =>
  clientType === ClientKind.SoleTrader || clientType === ClientKind.Partnership;

/** Reads a client's type once per client (module-level cache, like the Settings menu's community-interest flag). */
export function loadClientKind(clientId: string): Promise<ClientKindInfo | null> {
  const known = cache.get(clientId);
  if (known) return Promise.resolve(known);
  let pending = loading.get(clientId);
  if (!pending) {
    pending = Promise.resolve()
      .then(() => practiceService.getClient(clientId))
      .then((detail) => {
        const info: ClientKindInfo = {
          clientType: typeof detail?.clientType === 'number' ? detail.clientType : ClientKind.LimitedCompany,
          isCommunityInterest: pendingCommunityInterest.get(clientId) ?? !!detail?.isCommunityInterest,
          companyType: detail?.companyType ?? null,
          businessStartDate: detail?.businessStartDate ?? null,
          registrationNumber: detail?.registrationNumber ?? null,
        };
        pendingCommunityInterest.delete(clientId);
        cache.set(clientId, info);
        return info;
      })
      .catch(() => null)
      .finally(() => loading.delete(clientId));
    loading.set(clientId, pending);
  }
  return pending;
}

/** The client's type; null until it has been read (pages then show the limited-company layout). */
export function useClientKind(clientId: string | null | undefined): ClientKindInfo | null {
  const [info, setInfo] = useState<ClientKindInfo | null>(() => (clientId ? (cache.get(clientId) ?? null) : null));
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    void loadClientKind(clientId).then((loaded) => {
      if (!cancelled && loaded) setInfo(loaded);
    });
    // BUG-027: follow a flag changed on Company Info after the kind was read.
    const follow = () => {
      const known = cache.get(clientId);
      if (!cancelled && known) setInfo(known);
    };
    listeners.add(follow);
    return () => {
      cancelled = true;
      listeners.delete(follow);
    };
  }, [clientId]);
  return info;
}
